/*
 * Enth Projects — Shipping Extras
 * ---------------------------------------------------------------------------
 * Handles two custom checkout behaviors on artwork product pages:
 *
 *   1. "Add custom protective crate" checkbox → on Add-to-Cart submit, the
 *      crate is added as a second cart line with line item properties that
 *      link it back to the parent artwork.
 *
 *   2. "Request fully insured delivery quote" button → opens a pre-filled
 *      mailto window for high-value works that exceed the insurance cap.
 *
 * Safe to load even if there is no shipping-extras UI on the page.
 * Loaded as a deferred script — runs after DOM is parsed.
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-enth-shipping-extras]');
  if (!root) return;

  var productId = root.dataset.productId;
  var productTitle = root.dataset.productTitle || '';
  var productPrice = root.dataset.productPrice || '';
  var productVendor = root.dataset.productVendor || '';
  var productUrl = root.dataset.productUrl || window.location.href;
  var quoteEmail = root.dataset.quoteEmail || '';

  // -------------------------------------------------------------------------
  // 1. Quote button — mailto with prefilled subject and body
  // -------------------------------------------------------------------------
  var quoteBtn = root.querySelector('[data-enth-quote-trigger]');
  if (quoteBtn && quoteEmail) {
    quoteBtn.addEventListener('click', function () {
      var subject = 'Request fully insured delivery quote — ' + productTitle;
      var body =
        'Hello,\n\n' +
        'I would like to request a fully insured delivery quote for the following artwork:\n\n' +
        'Artwork: ' + productTitle + '\n' +
        'Artist: ' + productVendor + '\n' +
        'Price: ' + productPrice + '\n' +
        'URL: ' + productUrl + '\n\n' +
        'Please let me know the available shipping options, delivery timeframe, and full insurance coverage details.\n\n' +
        'Best regards,';

      var href =
        'mailto:' + encodeURIComponent(quoteEmail) +
        '?subject=' + encodeURIComponent(subject) +
        '&body=' + encodeURIComponent(body);

      window.location.href = href;
    });
  }

  // -------------------------------------------------------------------------
  // 2. Crate add-on — intercept Add to Cart, do a multi-item cart/add
  // -------------------------------------------------------------------------
  var crateToggle = root.querySelector('[data-enth-crate-toggle]');
  if (!crateToggle) return;

  // The snippet sits inside .artwork-info__actions, right above the
  // <product-form>. Find that form so we can intercept its submit.
  var actions = root.closest('.artwork-info__actions') || root.parentElement;
  if (!actions) return;

  var productForm = actions.querySelector('product-form form');
  if (!productForm) return;

  // Capture phase so we run before the <product-form> webcomponent listener.
  productForm.addEventListener(
    'submit',
    function (evt) {
      if (!crateToggle.checked) return; // crate unchecked — normal flow

      evt.preventDefault();
      evt.stopImmediatePropagation();

      var submitBtn = productForm.querySelector('[type="submit"]');
      var variantInput = productForm.querySelector('[name="id"]');
      var artworkVariantId = variantInput && variantInput.value;
      var crateVariantId = crateToggle.dataset.crateVariantId;
      if (!artworkVariantId || !crateVariantId) {
        // Missing data — fall back to standard add (artwork only)
        productForm.submit();
        return;
      }

      var pairingId = productId + '-' + Date.now();

      // Loading state on the Add to Cart button
      if (submitBtn) {
        submitBtn.setAttribute('aria-disabled', 'true');
        submitBtn.classList.add('loading');
        var spinner = submitBtn.querySelector('.loading__spinner');
        if (spinner) spinner.classList.remove('hidden');
      }

      // Find a cart instance to update (notification on desktop, drawer in
      // drawer mode). Falls back to a hard reload if neither exists.
      var cart =
        document.querySelector('cart-notification') ||
        document.querySelector('cart-drawer');

      var sectionsToRender = [];
      if (cart && typeof cart.getSectionsToRender === 'function') {
        sectionsToRender = cart.getSectionsToRender().map(function (s) {
          return s.id;
        });
      }

      var payload = {
        items: [
          {
            id: parseInt(artworkVariantId, 10),
            quantity: 1,
            properties: {
              '_pairing_id': pairingId
            }
          },
          {
            id: parseInt(crateVariantId, 10),
            quantity: 1,
            properties: {
              '_for_artwork': productTitle,
              '_artwork_id': productId,
              '_pairing_id': pairingId
            }
          }
        ]
      };
      if (sectionsToRender.length) {
        payload.sections = sectionsToRender.join(',');
        payload.sections_url = window.location.pathname;
      }

      var addUrl =
        (window.Shopify && window.Shopify.routes && window.Shopify.routes.root
          ? window.Shopify.routes.root
          : '/') + 'cart/add.js';

      // Cart-notification's renderContents relies on `parsedState.key` which
      // is undefined when items are added via `items[]` (multi-item add). To
      // avoid a crash we patch the response with the first added item's key
      // so cart-notification can locate the product preview section.
      var patchResponseForNotification = function (response) {
        if (
          cart &&
          cart.tagName === 'CART-NOTIFICATION' &&
          response &&
          response.items &&
          response.items.length &&
          !response.key
        ) {
          // Use the artwork (first item) as the previewed line
          response.key = response.items[0].key;
        }
        return response;
      };

      fetch(addUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(payload)
      })
        .then(function (response) {
          return response.json();
        })
        .then(function (response) {
          if (response.status) {
            console.error('[enth-shipping-extras] cart/add error', response);
            return;
          }

          response = patchResponseForNotification(response);

          // Notify the theme's PubSub system so other listeners refresh
          if (
            typeof window.publish === 'function' &&
            typeof window.PUB_SUB_EVENTS !== 'undefined' &&
            window.PUB_SUB_EVENTS.cartUpdate
          ) {
            window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
              source: 'enth-shipping-extras',
              productVariantId: artworkVariantId,
              cartData: response
            });
          }

          // Render new sections into the cart drawer / notification.
          // Note: renderContents calls open() internally via setTimeout, so we
          // don't need an extra cart.open() call here.
          if (cart && typeof cart.renderContents === 'function') {
            cart.renderContents(response);
          } else {
            // No cart instance found (e.g. drawer disabled) — navigate to cart
            window.location.href =
              (window.routes && window.routes.cart_url) || '/cart';
          }
        })
        .catch(function (err) {
          console.error('[enth-shipping-extras] fetch error', err);
        })
        .finally(function () {
          if (submitBtn) {
            submitBtn.classList.remove('loading');
            submitBtn.removeAttribute('aria-disabled');
            var spinner = submitBtn.querySelector('.loading__spinner');
            if (spinner) spinner.classList.add('hidden');
          }

          // CRITICAL: mirror product-form.js — remove the `is-empty` class
          // that the cart-drawer keeps when the cart was empty before this
          // add. Without this, CSS hides the freshly rendered items.
          if (cart && cart.classList && cart.classList.contains('is-empty')) {
            cart.classList.remove('is-empty');
          }
        });
    },
    true // capture phase
  );
})();
